export const RAID_CONTROL_TETRIS_SHARE_REF = '__doroparty_tetris_share_v1__';

const MODE_LABEL = {
  nearest: '오차 최소',
  kill: '막타 최소',
};

const text = (value) => String(value ?? '').trim();
const number = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

function normalizeMode(value) {
  const mode = text(value);
  return MODE_LABEL[mode] ? mode : 'nearest';
}

function normalizeScope(value) {
  return text(value) === 'global' ? 'global' : 'single';
}

function normalizeDeck(value) {
  return (Array.isArray(value) ? value : []).map((item) => text(item)).filter(Boolean).slice(0, 5);
}

function normalizeItem(raw = {}) {
  return {
    id: text(raw.id ?? raw.practiceRecordId ?? raw.practice_record_id),
    memberOpenid: text(raw.memberOpenid ?? raw.member_openid),
    nickname: text(raw.nickname ?? raw.memberNickname ?? raw.member_nickname) || '멤버',
    slotNo: Math.max(1, number(raw.slotNo ?? raw.slot_no, 1)),
    title: text(raw.title) || `모의전 ${Math.max(1, number(raw.slotNo ?? raw.slot_no, 1))}`,
    damageEok: Math.max(0, number(raw.damageEok ?? raw.damage_eok ?? raw.damage)),
    deck: normalizeDeck(raw.deck ?? raw.deckCodes ?? raw.deck_codes),
  };
}

export function raidControlTetrisModeLabel(mode) {
  return MODE_LABEL[normalizeMode(mode)];
}

export function normalizeRaidControlTetrisShare(raw = null) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const bossNo = number(raw.bossNo ?? raw.boss_no);
  const targetHpEok = Math.max(0, number(raw.targetHpEok ?? raw.target_hp_eok));
  const items = (Array.isArray(raw.items) ? raw.items : []).map(normalizeItem)
    .filter((item) => item.id && item.memberOpenid && item.damageEok > 0 && item.deck.length === 5)
    .slice(0, 24);
  if (bossNo < 1 || bossNo > 5 || targetHpEok <= 0 || !items.length) return null;
  const totalEok = Math.max(0, number(raw.totalEok ?? raw.total_eok, items.reduce((sum, item) => sum + item.damageEok, 0)));
  const diffEok = number(raw.diffEok ?? raw.diff_eok, totalEok - targetHpEok);
  const scope = normalizeScope(raw.scope ?? raw.planScope ?? raw.plan_scope);
  const requestedPlanCount = Math.max(1, Math.trunc(number(raw.planCount ?? raw.plan_count, 1)));
  const planCount = scope === 'global' ? requestedPlanCount : 1;
  const requestedPlanIndex = Math.max(0, Math.trunc(number(raw.planIndex ?? raw.plan_index, 0)));
  const planIndex = scope === 'global' ? Math.min(requestedPlanIndex, planCount - 1) : 0;
  return {
    version: 1,
    scope,
    planIndex,
    planCount,
    mode: normalizeMode(raw.mode),
    preferAvailability: Boolean(raw.preferAvailability ?? raw.prefer_availability),
    currentAvailabilityOnly: Boolean(raw.currentAvailabilityOnly ?? raw.current_availability_only),
    seasonNo: Math.max(0, number(raw.seasonNo ?? raw.season_no)),
    bossNo,
    bossName: text(raw.bossName ?? raw.boss_name) || '보스',
    phaseNo: Math.max(1, number(raw.phaseNo ?? raw.phase_no, 1)),
    targetHpEok,
    totalEok,
    diffEok,
    sufficient: totalEok >= targetHpEok,
    opportunityCostEok: Math.max(0, number(raw.opportunityCostEok ?? raw.opportunity_cost_eok)),
    candidateCount: Math.max(items.length, number(raw.candidateCount ?? raw.candidate_count, items.length)),
    items,
  };
}

export function createRaidControlTetrisShareRef(rawShare) {
  const share = normalizeRaidControlTetrisShare(rawShare);
  if (!share) return null;
  return {
    nikkeId: RAID_CONTROL_TETRIS_SHARE_REF,
    name: `테트리스 ${raidControlTetrisModeLabel(share.mode)}`,
    tetrisShare: share,
  };
}

export function isRaidControlTetrisShareRef(raw = null) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const id = text(raw.nikkeId ?? raw.nikke_id ?? raw.code ?? raw.id);
  return id === RAID_CONTROL_TETRIS_SHARE_REF || Boolean(raw.tetrisShare ?? raw.tetris_share);
}

export function extractRaidControlTetrisShare(rawRefs = []) {
  const source = (Array.isArray(rawRefs) ? rawRefs : []).find(isRaidControlTetrisShareRef);
  if (!source) return null;
  return normalizeRaidControlTetrisShare(source.tetrisShare ?? source.tetris_share);
}

function formatEok(value) {
  return `${number(value).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

export function raidControlTetrisShareBody(rawShare) {
  const share = normalizeRaidControlTetrisShare(rawShare);
  if (!share) return '';
  const diff = share.diffEok;
  const diffLabel = diff >= 0 ? `초과 +${formatEok(diff)}` : `부족 ${formatEok(Math.abs(diff))}`;
  return `테트리스 ${raidControlTetrisModeLabel(share.mode)} · ${share.bossName} ${share.phaseNo}단 · 목표 ${formatEok(share.targetHpEok)} · 합계 ${formatEok(share.totalEok)} · ${diffLabel} · ${share.items.length}타`;
}
